import React, { useState, useEffect, useMemo } from 'react';
import {
  DndContext,
  DragOverlay,
  useSensor,
  useSensors,
  PointerSensor,
  useDraggable,
  useDroppable,
  DragStartEvent,
  DragEndEvent,
  closestCenter,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  horizontalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { apiUrl } from '../../utils/apiBase';

// ========================
// TYPES
// ========================
type FieldRole = 'dimension' | 'metric' | 'both';

interface CubeField {
  id: string;
  name: string;
  source: 'fluxo' | 'etapa' | 'campo' | 'card' | 'tarefa' | 'sla';
  sourceId?: string;
  sourceName?: string;
  fieldType?: string;
  etapaId?: string;
  etapaNome?: string;
  fluxoId?: string;
  fluxoNome?: string;
  role: FieldRole;
}

interface CubeReport {
  id: string;
  name: string;
  description?: string;
  rows: CubeField[];
  columns: CubeField[];
  values: CubeField[];
  filters: CubeFilter[];
  createdAt: string;
  updatedAt: string;
}

interface CubeFilter {
  fieldId: string;
  operator: 'igual' | 'diferente' | 'contem' | 'vazio' | 'preenchido' | 'maior' | 'menor';
  value: string;
}

interface CubeProps {
  flows: any[];
  session: any;
}

// ========================
// HELPERS
// ========================

function getFieldRole(fieldType?: string, fieldId?: string): FieldRole {
  const numericTypes = ['number', 'moeda', 'valor', 'porcentagem', 'currency'];
  if (fieldType && numericTypes.includes(fieldType)) return 'metric';
  const dimensionTypes = ['text', 'select', 'radio', 'checkbox', 'telefone', 'email', 'cpf', 'cnpj'];
  if (fieldType && dimensionTypes.includes(fieldType)) return 'dimension';
  if (fieldType === 'date' || fieldType === 'data') return 'both';
  if (fieldId) {
    if (['_sla_tempoNaEtapaAtual', '_sla_tempoMaximoEtapa', '_sla_percentualSLA', '_sla_horasAtrasado',
         '_sla_tempoTotalNoFluxo', '_sla_etapasPercorridas', '_sla_retornos'].includes(fieldId)) return 'metric';
    if (['_sla_etapaAtual', '_sla_statusSLA', '_sla_responsavelAtual', '_sla_movidoPor', '_sla_motivoUltimaMovimentacao'].includes(fieldId)) return 'dimension';
    if (['_card_id', '_card_titulo', '_card_responsavel', '_card_status', '_card_prioridade'].includes(fieldId)) return 'dimension';
    if (['_card_criadoEm', '_card_atualizadoEm', '_sla_dataEntradaEtapa', '_sla_ultimaMovimentacao'].includes(fieldId)) return 'both';
    if (fieldId.startsWith('_fluxo_') || fieldId.startsWith('_etapa_')) return 'dimension';
  }
  return 'both';
}

function canBeRowOrCol(field: CubeField): boolean {
  return field.role === 'dimension' || field.role === 'both';
}

function canBeValue(field: CubeField): boolean {
  return field.role === 'metric' || field.role === 'both';
}

function getAllowedZones(field: CubeField): string[] {
  const zones: string[] = [];
  if (canBeRowOrCol(field)) { zones.push('rows', 'columns'); }
  if (canBeValue(field)) { zones.push('values'); }
  if (zones.length === 0) return ['rows', 'columns', 'values'];
  return zones;
}

const roleConfig: Record<FieldRole, { label: string; icon: string; bg: string; text: string; border: string }> = {
  dimension: { label: 'Dimensao', icon: '\u{1F4CA}', bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
  metric: { label: 'Metrica', icon: '\u{1F4C8}', bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' },
  both: { label: 'Ambos', icon: '\u{1F504}', bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200' },
};

const sourceColors: Record<string, string> = {
  fluxo: 'bg-purple-100 text-purple-800 border-purple-300',
  etapa: 'bg-blue-100 text-blue-800 border-blue-300',
  campo: 'bg-green-100 text-green-800 border-green-300',
  card: 'bg-amber-100 text-amber-800 border-amber-300',
  tarefa: 'bg-pink-100 text-pink-800 border-pink-300',
  sla: 'bg-red-100 text-red-800 border-red-300',
};

// ========================
// FIELD CHIP (static display)
// ========================
const FieldChip: React.FC<{ field: CubeField; onRemove?: () => void; isDragging?: boolean }> = ({ field, onRemove, isDragging }) => {
  const chipClasses = `inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border text-xs font-medium select-none ${sourceColors[field.source] || 'bg-gray-100 text-gray-700 border-gray-300'} ${isDragging ? 'shadow-lg ring-2 ring-indigo-400 opacity-80' : 'shadow-sm'}`;
  return (
    <div className={chipClasses}>
      <svg className="h-3 w-3 opacity-50" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
      </svg>
      <span className="truncate max-w-[140px]">{field.name}</span>
      {onRemove && (
        <button onClick={(e) => { e.stopPropagation(); onRemove(); }} className="ml-0.5 hover:text-red-600 opacity-60 hover:opacity-100">
          <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
    </div>
  );
};

// ========================
// SORTABLE FIELD (inside zones - supports reordering)
// ========================
const SortableField: React.FC<{ field: CubeField; onRemove?: () => void }> = ({ field, onRemove }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: field.id,
    data: { field, fromZone: true },
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners} className={`cursor-grab active:cursor-grabbing ${isDragging ? 'opacity-40 z-50' : ''}`}>
      <FieldChip field={field} onRemove={onRemove} />
    </div>
  );
};

// ========================
// SORTABLE TABLE HEADER (drag to reorder columns)
// ========================
const SortableTableHeader: React.FC<{ id: string; header: string }> = ({ id, header }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  return (
    <th
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`px-4 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap cursor-grab active:cursor-grabbing select-none ${isDragging ? 'opacity-40 bg-indigo-50' : 'hover:bg-gray-100'}`}
    >
      <div className="flex items-center gap-1">
        <svg className="h-3 w-3 text-gray-300" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 15L12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9" />
        </svg>
        {header}
      </div>
    </th>
  );
};

// ========================
// DROP ZONE with compatibility feedback
// ========================
const DropZone: React.FC<{
  id: string;
  title: string;
  subtitle: string;
  helpText: string;
  color: string;
  icon: React.ReactNode;
  fields: CubeField[];
  onRemoveField: (fieldId: string) => void;
  activeField: CubeField | null;
}> = ({ id, title, subtitle, helpText, color, icon, fields, onRemoveField, activeField }) => {
  const { setNodeRef, isOver } = useDroppable({ id });
  const isCompatible = activeField ? getAllowedZones(activeField).includes(id) : true;
  const isInvalidDrop = activeField && !isCompatible;

  const colorMap: Record<string, { normal: string; over: string; invalid: string }> = {
    blue: { normal: 'border-blue-300 bg-blue-50/50', over: 'border-blue-500 bg-blue-100 ring-2 ring-blue-300', invalid: 'border-red-300 bg-red-50/30 opacity-50' },
    green: { normal: 'border-green-300 bg-green-50/50', over: 'border-green-500 bg-green-100 ring-2 ring-green-300', invalid: 'border-red-300 bg-red-50/30 opacity-50' },
    amber: { normal: 'border-amber-300 bg-amber-50/50', over: 'border-amber-500 bg-amber-100 ring-2 ring-amber-300', invalid: 'border-red-300 bg-red-50/30 opacity-50' },
  };

  const colors = colorMap[color] || { normal: 'border-gray-300 bg-gray-50/50', over: 'border-gray-500 bg-gray-100 ring-2 ring-gray-300', invalid: 'border-red-300 bg-red-50/30 opacity-50' };
  const borderClass = isInvalidDrop ? colors.invalid : (isOver && isCompatible ? colors.over : colors.normal);

  return (
    <div ref={setNodeRef} className={`border-2 border-dashed rounded-lg p-3 min-h-[90px] transition-all duration-200 ${borderClass}`}>
      <div className="flex items-center gap-1.5 mb-1">
        {icon}
        <span className="text-xs font-semibold text-gray-700">{title}</span>
        <span className="text-[0.6rem] text-gray-400 font-medium">{subtitle}</span>
      </div>
      <p className="text-[0.55rem] text-gray-400 mb-2 leading-tight">{helpText}</p>
      <SortableContext items={fields.map(f => f.id)} strategy={horizontalListSortingStrategy}>
        <div className="flex flex-wrap gap-1.5">
          {fields.length === 0 ? (
            <span className={`text-xs italic ${isOver && isCompatible ? 'text-gray-600 font-medium' : isInvalidDrop ? 'text-red-400' : 'text-gray-400'}`}>
              {isOver && isCompatible ? 'Solte aqui!' : isInvalidDrop ? 'Tipo incompativel' : 'Arraste campos aqui...'}
            </span>
          ) : (
            fields.map(f => (
              <SortableField key={f.id} field={f} onRemove={() => onRemoveField(f.id)} />
            ))
          )}
        </div>
      </SortableContext>
    </div>
  );
};

// ========================
// SOURCE FIELD ITEM (left panel - context-aware buttons)
// ========================
const SourceFieldItem: React.FC<{
  field: CubeField;
  addFieldToZone: (field: CubeField, zone: string) => void;
}> = ({ field, addFieldToZone }) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `source-${field.id}`,
    data: { field },
  });

  const allowed = getAllowedZones(field);
  const rc = roleConfig[field.role];

  return (
    <div ref={setNodeRef} className={`group flex items-center gap-1 px-2 py-1.5 rounded-md hover:bg-white hover:shadow-sm text-xs text-gray-700 transition-colors ${isDragging ? 'opacity-40' : ''}`}>
      <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing flex items-center gap-1.5 flex-1 min-w-0">
        <svg className="h-3 w-3 text-gray-300 group-hover:text-gray-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
        </svg>
        <span className="truncate flex-1">{field.name}</span>
      </div>
      <span className={`text-[0.5rem] px-1 py-0.5 rounded ${rc.bg} ${rc.text} font-medium flex-shrink-0 hidden group-hover:inline`}>
        {rc.icon}
      </span>
      <div className="hidden group-hover:flex items-center gap-0.5 flex-shrink-0">
        {allowed.includes('rows') && (
          <button onClick={() => addFieldToZone(field, 'rows')} title="Adicionar as Linhas (agrupar)" className="p-0.5 rounded bg-blue-100 text-blue-600 hover:bg-blue-200">
            <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M3.75 12h16.5m-16.5 3.75h16.5M3.75 19.5h16.5M5.625 4.5h12.75a1.875 1.875 0 010 3.75H5.625a1.875 1.875 0 010-3.75z" /></svg>
          </button>
        )}
        {allowed.includes('columns') && (
          <button onClick={() => addFieldToZone(field, 'columns')} title="Adicionar as Colunas (dividir)" className="p-0.5 rounded bg-green-100 text-green-600 hover:bg-green-200">
            <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M9 4.5v15m6-15v15m-10.875 0h15.75c.621 0 1.125-.504 1.125-1.125V5.625c0-.621-.504-1.125-1.125-1.125H4.125C3.504 4.5 3 5.004 3 5.625v12.75c0 .621.504 1.125 1.125 1.125z" /></svg>
          </button>
        )}
        {allowed.includes('values') && (
          <button onClick={() => addFieldToZone(field, 'values')} title="Adicionar aos Valores (medir)" className="p-0.5 rounded bg-amber-100 text-amber-600 hover:bg-amber-200">
            <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 15.75V18m-7.5-6.75h.008v.008H8.25v-.008zm0 2.25h.008v.008H8.25V13.5zm0 2.25h.008v.008H8.25v-.008zm0 2.25h.008v.008H8.25V18zm2.498-6.75h.007v.008h-.007v-.008zm0 2.25h.007v.008h-.007V13.5zm0 2.25h.007v.008h-.007v-.008zm0 2.25h.007v.008h-.007V18zm2.504-6.75h.008v.008h-.008v-.008zm0 2.25h.008v.008h-.008V13.5zm0 2.25h.008v.008h-.008v-.008zm0 2.25h.008v.008h-.008V18zm2.498-6.75h.008v.008h-.008v-.008zm0 2.25h.008v.008h-.008V13.5zM8.25 6h7.5v2.25h-7.5V6zM12 2.25c-1.892 0-3.758.11-5.593.322C5.307 2.7 4.5 3.65 4.5 4.757V19.5a2.25 2.25 0 002.25 2.25h10.5a2.25 2.25 0 002.25-2.25V4.757c0-1.108-.806-2.057-1.907-2.185A48.507 48.507 0 0012 2.25z" /></svg>
          </button>
        )}
      </div>
    </div>
  );
};

// ========================
// FILTER ROW
// ========================
const FilterRow: React.FC<{
  filter: CubeFilter;
  availableFields: CubeField[];
  onChange: (f: CubeFilter) => void;
  onRemove: () => void;
}> = ({ filter, availableFields, onChange, onRemove }) => {
  return (
    <div className="flex items-center gap-2 bg-gray-50 p-2 rounded-md">
      <select value={filter.fieldId} onChange={(e) => onChange({ ...filter, fieldId: e.target.value })} className="px-2 py-1 border border-gray-300 rounded text-xs bg-white min-w-[120px]">
        <option value="">Campo...</option>
        {availableFields.map(f => (
          <option key={f.id} value={f.id}>{f.name} ({f.sourceName})</option>
        ))}
      </select>
      <select value={filter.operator} onChange={(e) => onChange({ ...filter, operator: e.target.value as CubeFilter['operator'] })} className="px-2 py-1 border border-gray-300 rounded text-xs bg-white">
        <option value="igual">Igual a</option>
        <option value="diferente">Diferente de</option>
        <option value="contem">Contem</option>
        <option value="vazio">Vazio</option>
        <option value="preenchido">Preenchido</option>
        <option value="maior">Maior que</option>
        <option value="menor">Menor que</option>
      </select>
      {!['vazio', 'preenchido'].includes(filter.operator) && (
        <input type="text" value={filter.value} onChange={(e) => onChange({ ...filter, value: e.target.value })} placeholder="Valor..." className="px-2 py-1 border border-gray-300 rounded text-xs bg-white min-w-[100px]" />
      )}
      <button onClick={onRemove} className="text-red-400 hover:text-red-600">
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
};

// ========================
// MAIN CUBE COMPONENT
// ========================
const Cube: React.FC<CubeProps> = ({ flows, session }) => {
  const [availableFields, setAvailableFields] = useState<CubeField[]>([]);
  const [reportName, setReportName] = useState('Novo Relatorio');
  const [rowFields, setRowFields] = useState<CubeField[]>([]);
  const [colFields, setColFields] = useState<CubeField[]>([]);
  const [valueFields, setValueFields] = useState<CubeField[]>([]);
  const [filters, setFilters] = useState<CubeFilter[]>([]);
  const [savedReports, setSavedReports] = useState<CubeReport[]>([]);
  const [selectedReportId, setSelectedReportId] = useState<string | null>(null);
  const [tableData, setTableData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasGenerated, setHasGenerated] = useState(false);
  const [activeField, setActiveField] = useState<CubeField | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFlowId, setSelectedFlowId] = useState<string>('');
  const [showResults, setShowResults] = useState(false);
  const [roleFilter, setRoleFilter] = useState<'all' | 'dimension' | 'metric'>('all');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showZones, setShowZones] = useState(true);
  const [showFilters, setShowFilters] = useState(true);
  // Column order for the results table
  const [columnOrder, setColumnOrder] = useState<string[]>([]);

  // ── Tour state ──
  const [showTour, setShowTour] = useState(false);
  const [tourStep, setTourStep] = useState(0);
  const [tourPaused, setTourPaused] = useState(false);

  const tourSteps = [
    {
      icon: '🧊',
      title: 'Bem-vindo ao Cube!',
      subtitle: 'Construtor de Relatórios Dinâmicos',
      content: 'O Cube permite criar relatórios customizados cruzando qualquer dado dos seus fluxos. Arraste campos para montar tabelas dinâmicas como em uma planilha profissional.',
      visual: 'intro',
    },
    {
      icon: '📋',
      title: 'Painel de Campos',
      subtitle: 'Seus dados organizados',
      content: 'No painel esquerdo ficam todos os campos disponíveis: dados do card, SLA, etapas e campos personalizados dos seus fluxos. Use o filtro para encontrar rapidamente o que precisa.',
      visual: 'fields',
    },
    {
      icon: '📊',
      title: 'Linhas, Colunas e Valores',
      subtitle: 'Monte sua tabela',
      content: 'Arraste ou clique nos campos para distribuí-los nas 3 zonas. Linhas agrupam por categoria, Colunas dividem horizontalmente e Valores são os dados numéricos medidos.',
      visual: 'zones',
    },
    {
      icon: '🔍',
      title: 'Filtros Inteligentes',
      subtitle: 'Refine seus dados',
      content: 'Adicione filtros para mostrar apenas os dados que importam. Filtre por responsável, status, fluxo específico ou qualquer outro campo.',
      visual: 'filters',
    },
    {
      icon: '⚡',
      title: 'Gerar e Exportar',
      subtitle: 'Resultados instantâneos',
      content: 'Clique em "Gerar Relatório" para ver os resultados em tabela. Você pode reordenar colunas arrastando os cabeçalhos e exportar tudo para CSV.',
      visual: 'generate',
    },
    {
      icon: '💾',
      title: 'Salvar Relatórios',
      subtitle: 'Acesse depois',
      content: 'Dê um nome ao seu relatório e clique em "Salvar". Seus relatórios ficam salvos e podem ser recarregados a qualquer momento no painel esquerdo.',
      visual: 'save',
    },
    {
      icon: '💡',
      title: 'Dica: Exemplo Prático',
      subtitle: 'Comece assim',
      content: 'Tente: Linhas = Responsável, Colunas = Status SLA, Valores = Horas em Atraso. Você terá uma visão de quanto cada pessoa está atrasada por status. Combine campos à vontade!',
      visual: 'tip',
    },
  ];

  // Auto-show tour on first visit
  useEffect(() => {
    if (!session?.usuario) return;
    const tourKey = `cube_tour_dismissed_${session.usuario.id || session.usuario.email}`;
    if (!localStorage.getItem(tourKey)) {
      setShowTour(true);
      localStorage.setItem(tourKey, 'true');
    }
  }, [session?.usuario]);

  // Tour auto-advance
  useEffect(() => {
    if (!showTour || tourPaused || tourStep >= tourSteps.length - 1) return;
    const timer = setTimeout(() => setTourStep(prev => prev + 1), 14000);
    return () => clearTimeout(timer);
  }, [showTour, tourStep, tourPaused]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  // Build available fields from flows
  useEffect(() => {
    const fields: CubeField[] = [];

    // Card meta
    fields.push(
      { id: '_card_id', name: 'ID do Card', source: 'card', sourceName: 'Card', role: 'dimension' },
      { id: '_card_titulo', name: 'Titulo do Card', source: 'card', sourceName: 'Card', role: 'dimension' },
      { id: '_card_criadoEm', name: 'Data de Criacao', source: 'card', sourceName: 'Card', fieldType: 'date', role: 'both' },
      { id: '_card_atualizadoEm', name: 'Ultima Atualizacao', source: 'card', sourceName: 'Card', fieldType: 'date', role: 'both' },
      { id: '_card_responsavel', name: 'Responsavel', source: 'card', sourceName: 'Card', role: 'dimension' },
      { id: '_card_status', name: 'Status', source: 'card', sourceName: 'Card', role: 'dimension' },
      { id: '_card_prioridade', name: 'Prioridade', source: 'card', sourceName: 'Card', role: 'dimension' },
    );

    // SLA - Dimensoes
    fields.push(
      { id: '_sla_etapaAtual', name: 'Etapa Atual', source: 'sla', sourceName: 'SLA', fieldType: 'text', role: 'dimension' },
      { id: '_sla_statusSLA', name: 'Status SLA', source: 'sla', sourceName: 'SLA', fieldType: 'text', role: 'dimension' },
      { id: '_sla_responsavelAtual', name: 'Responsavel Atual', source: 'sla', sourceName: 'SLA', fieldType: 'text', role: 'dimension' },
      { id: '_sla_movidoPor', name: 'Movido Por (Ultimo)', source: 'sla', sourceName: 'SLA', fieldType: 'text', role: 'dimension' },
      { id: '_sla_motivoUltimaMovimentacao', name: 'Motivo Ultima Mov.', source: 'sla', sourceName: 'SLA', fieldType: 'text', role: 'dimension' },
    );

    // SLA - Metricas
    fields.push(
      { id: '_sla_tempoNaEtapaAtual', name: 'Tempo na Etapa Atual (h)', source: 'sla', sourceName: 'SLA', fieldType: 'number', role: 'metric' },
      { id: '_sla_tempoMaximoEtapa', name: 'SLA da Etapa (h)', source: 'sla', sourceName: 'SLA', fieldType: 'number', role: 'metric' },
      { id: '_sla_percentualSLA', name: '% SLA Consumido', source: 'sla', sourceName: 'SLA', fieldType: 'number', role: 'metric' },
      { id: '_sla_horasAtrasado', name: 'Horas em Atraso', source: 'sla', sourceName: 'SLA', fieldType: 'number', role: 'metric' },
      { id: '_sla_tempoTotalNoFluxo', name: 'Tempo Total no Fluxo (h)', source: 'sla', sourceName: 'SLA', fieldType: 'number', role: 'metric' },
      { id: '_sla_etapasPercorridas', name: 'Qtd Etapas Percorridas', source: 'sla', sourceName: 'SLA', fieldType: 'number', role: 'metric' },
      { id: '_sla_retornos', name: 'Qtd Retornos', source: 'sla', sourceName: 'SLA', fieldType: 'number', role: 'metric' },
    );

    // SLA - Datas
    fields.push(
      { id: '_sla_dataEntradaEtapa', name: 'Data Entrada na Etapa', source: 'sla', sourceName: 'SLA', fieldType: 'date', role: 'both' },
      { id: '_sla_ultimaMovimentacao', name: 'Ultima Movimentacao', source: 'sla', sourceName: 'SLA', fieldType: 'date', role: 'both' },
    );

    // Fluxos, Etapas, Campos
    flows.forEach(flow => {
      fields.push({
        id: `_fluxo_${flow.id}`,
        name: flow.nome || flow.name,
        source: 'fluxo',
        sourceName: 'Fluxo',
        fluxoId: flow.id,
        fluxoNome: flow.nome || flow.name,
        role: 'dimension',
      });

      (flow.etapas || []).forEach((etapa: any) => {
        fields.push({
          id: `_etapa_${etapa.id}`,
          name: etapa.nome,
          source: 'etapa',
          sourceName: flow.nome || flow.name,
          etapaId: etapa.id,
          fluxoId: flow.id,
          fluxoNome: flow.nome || flow.name,
          role: 'dimension',
        });

        (etapa.campos || []).forEach((campo: any) => {
          const role = getFieldRole(campo.tipo, undefined);
          fields.push({
            id: `campo_${flow.id}_${etapa.id}_${campo.id}`,
            name: campo.nome,
            source: 'campo',
            sourceName: `${etapa.nome}`,
            fieldType: campo.tipo,
            etapaId: etapa.id,
            etapaNome: etapa.nome,
            fluxoId: flow.id,
            fluxoNome: flow.nome || flow.name,
            role,
          });
        });
      });
    });

    setAvailableFields(fields);
  }, [flows]);

  // Load saved reports
  useEffect(() => {
    loadReports();
  }, []);

  const loadReports = async () => {
    try {
      const empresaId = session?.empresa?.id || session?.usuario?.empresaId;
      if (!empresaId) return;
      const resp = await fetch(apiUrl(`/cube/reports?empresaId=${empresaId}`));
      if (resp.ok) {
        const data = await resp.json();
        setSavedReports(data.reports || []);
      }
    } catch (_e) {
      console.log('Cube: sem relatorios salvos ainda');
    }
  };

  // Filter available fields
  const filteredFields = useMemo(() => {
    let result = availableFields;
    if (selectedFlowId) {
      result = result.filter(f => !f.fluxoId || f.fluxoId === selectedFlowId || f.source === 'card' || f.source === 'sla');
    }
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(f => f.name.toLowerCase().includes(term) || (f.sourceName || '').toLowerCase().includes(term));
    }
    if (roleFilter === 'dimension') {
      result = result.filter(f => f.role === 'dimension' || f.role === 'both');
    } else if (roleFilter === 'metric') {
      result = result.filter(f => f.role === 'metric' || f.role === 'both');
    }
    const usedIds = new Set([...rowFields.map(f => f.id), ...colFields.map(f => f.id), ...valueFields.map(f => f.id)]);
    result = result.filter(f => !usedIds.has(f.id));
    return result;
  }, [availableFields, selectedFlowId, searchTerm, roleFilter, rowFields, colFields, valueFields]);

  // Group fields by role
  const groupedFields = useMemo(() => {
    const groups: Record<string, CubeField[]> = {};
    const order = ['\u{1F4CA} Dimensoes (Linhas/Colunas)', '\u{1F4C8} Metricas (Valores)', '\u{1F504} Ambos (Linhas/Colunas/Valores)'];
    order.forEach(k => { groups[k] = []; });
    filteredFields.forEach(f => {
      const key = f.role === 'dimension' ? order[0] : f.role === 'metric' ? order[1] : order[2];
      groups[key].push(f);
    });
    order.forEach(k => { if (groups[k].length === 0) delete groups[k]; });
    return groups;
  }, [filteredFields]);

  // Drag handlers
  const handleDragStart = (event: DragStartEvent) => {
    const field = event.active.data.current?.field as CubeField | undefined;
    if (field) setActiveField(field);
  };

  /** Find which zone a field is currently in */
  const findFieldZone = (fieldId: string): { zone: string; fields: CubeField[]; setter: React.Dispatch<React.SetStateAction<CubeField[]>> } | null => {
    if (rowFields.some(f => f.id === fieldId)) return { zone: 'rows', fields: rowFields, setter: setRowFields };
    if (colFields.some(f => f.id === fieldId)) return { zone: 'columns', fields: colFields, setter: setColFields };
    if (valueFields.some(f => f.id === fieldId)) return { zone: 'values', fields: valueFields, setter: setValueFields };
    return null;
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveField(null);
    const { active, over } = event;
    if (!over || !active) return;

    const activeId = String(active.id);
    const overId = String(over.id);

    // --- Table column reorder ---
    if (activeId.startsWith('col-header-') && overId.startsWith('col-header-')) {
      const fromKey = activeId.replace('col-header-', '');
      const toKey = overId.replace('col-header-', '');
      if (fromKey !== toKey) {
        setColumnOrder(prev => {
          const oldIdx = prev.indexOf(fromKey);
          const newIdx = prev.indexOf(toKey);
          if (oldIdx === -1 || newIdx === -1) return prev;
          return arrayMove(prev, oldIdx, newIdx);
        });
      }
      return;
    }

    const field = active.data.current?.field as CubeField | undefined;
    if (!field) return;

    // --- Reorder within same zone ---
    const fromZone = findFieldZone(field.id);
    if (fromZone) {
      // Check if dropping on another field in the same zone
      const overInSameZone = fromZone.fields.find(f => f.id === overId);
      if (overInSameZone) {
        // Reorder within zone
        const oldIndex = fromZone.fields.findIndex(f => f.id === field.id);
        const newIndex = fromZone.fields.findIndex(f => f.id === overId);
        if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
          fromZone.setter(prev => arrayMove(prev, oldIndex, newIndex));
        }
        return;
      }
    }

    // --- Drop from source/zone into a zone ---
    const targetZone = String(over.id);
    if (['rows', 'columns', 'values'].includes(targetZone) && getAllowedZones(field).includes(targetZone)) {
      addFieldToZone(field, targetZone);
    }
  };

  const addFieldToZone = (field: CubeField, zone: string) => {
    if (!getAllowedZones(field).includes(zone)) return;
    setRowFields(prev => prev.filter(f => f.id !== field.id));
    setColFields(prev => prev.filter(f => f.id !== field.id));
    setValueFields(prev => prev.filter(f => f.id !== field.id));
    switch (zone) {
      case 'rows': setRowFields(prev => [...prev, field]); break;
      case 'columns': setColFields(prev => [...prev, field]); break;
      case 'values': setValueFields(prev => [...prev, field]); break;
    }
  };

  const removeFieldFromZone = (fieldId: string, zone: string) => {
    switch (zone) {
      case 'rows': setRowFields(prev => prev.filter(f => f.id !== fieldId)); break;
      case 'columns': setColFields(prev => prev.filter(f => f.id !== fieldId)); break;
      case 'values': setValueFields(prev => prev.filter(f => f.id !== fieldId)); break;
    }
  };

  // Generate report
  const generateReport = async () => {
    if (rowFields.length === 0 && colFields.length === 0) return;
    setIsLoading(true);
    setShowResults(true);
    try {
      const empresaId = session?.empresa?.id || session?.usuario?.empresaId;
      const resp = await fetch(apiUrl('/cube/query'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          empresaId,
          rows: rowFields.map(f => ({ id: f.id, name: f.name, source: f.source, fluxoId: f.fluxoId, etapaId: f.etapaId, fieldType: f.fieldType })),
          columns: colFields.map(f => ({ id: f.id, name: f.name, source: f.source, fluxoId: f.fluxoId, etapaId: f.etapaId, fieldType: f.fieldType })),
          values: valueFields.map(f => ({ id: f.id, name: f.name, source: f.source, fluxoId: f.fluxoId, etapaId: f.etapaId, fieldType: f.fieldType })),
          filters: filters.filter(f => f.fieldId),
          flowIds: selectedFlowId ? [selectedFlowId] : flows.map(f => f.id),
        }),
      });
      if (resp.ok) {
        const data = await resp.json();
        const results = data.results || [];
        setTableData(results);
        // Initialize column order from the keys of the first row
        if (results.length > 0) {
          setColumnOrder(Object.keys(results[0]));
        } else {
          setColumnOrder([]);
        }
        setHasGenerated(true);
      } else {
        console.error('Erro ao gerar relatorio');
        setTableData([]);
      }
    } catch (_e) {
      console.error('Erro ao gerar relatorio:', _e);
      setTableData([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Save report
  const saveReport = async () => {
    try {
      const empresaId = session?.empresa?.id || session?.usuario?.empresaId;
      const report: Partial<CubeReport> = {
        id: selectedReportId || `report_${Date.now()}`,
        name: reportName,
        rows: rowFields,
        columns: colFields,
        values: valueFields,
        filters,
        updatedAt: new Date().toISOString(),
      };
      if (!selectedReportId) { report.createdAt = new Date().toISOString(); }
      const resp = await fetch(apiUrl('/cube/reports'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ empresaId, report }),
      });
      if (resp.ok) {
        await loadReports();
        setSelectedReportId(report.id!);
      }
    } catch (_e) {
      console.error('Erro ao salvar relatorio:', _e);
    }
  };

  // Load report
  const loadReport = (report: CubeReport) => {
    setSelectedReportId(report.id);
    setReportName(report.name);
    const addRole = (f: CubeField) => ({ ...f, role: f.role || getFieldRole(f.fieldType, f.id) });
    setRowFields((report.rows || []).map(addRole));
    setColFields((report.columns || []).map(addRole));
    setValueFields((report.values || []).map(addRole));
    setFilters(report.filters || []);
    setShowResults(false);
    setHasGenerated(false);
    setTableData([]);
  };

  // New report
  const newReport = () => {
    setSelectedReportId(null);
    setReportName('Novo Relatorio');
    setRowFields([]);
    setColFields([]);
    setValueFields([]);
    setFilters([]);
    setShowResults(false);
    setHasGenerated(false);
    setTableData([]);
  };

  // Delete report
  const deleteReport = async (reportId: string) => {
    try {
      const empresaId = session?.empresa?.id || session?.usuario?.empresaId;
      await fetch(apiUrl(`/cube/reports/${reportId}?empresaId=${empresaId}`), { method: 'DELETE' });
      await loadReports();
      if (selectedReportId === reportId) newReport();
    } catch (_e) {
      console.error('Erro ao excluir relatorio:', _e);
    }
  };

  // Export CSV
  const exportCSV = () => {
    if (tableData.length === 0) return;
    // Use column order if available, otherwise fall back to object keys
    const headers = columnOrder.length > 0 ? columnOrder : Object.keys(tableData[0]);
    const csvRows = [
      headers.join(';'),
      ...tableData.map(row => headers.map(h => `"${String(row[h] ?? '').replace(/"/g, '""')}"`).join(';'))
    ];
    const blob = new Blob(['\uFEFF' + csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${reportName || 'relatorio'}_${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const dimensionCount = filteredFields.filter(f => f.role === 'dimension' || f.role === 'both').length;
  const metricCount = filteredFields.filter(f => f.role === 'metric' || f.role === 'both').length;

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b bg-white">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-indigo-100 rounded-lg">
            <svg className="h-6 w-6 text-indigo-600" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 7.5l-9-5.25L3 7.5m18 0l-9 5.25m9-5.25v9l-9 5.25M3 7.5l9 5.25M3 7.5v9l9 5.25m0-9v9" />
            </svg>
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Cube</h1>
            <p className="text-xs text-gray-500">Construtor de relatorios dinamicos</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => { setTourStep(0); setTourPaused(false); setShowTour(true); }} className="px-3 py-1.5 text-xs border border-indigo-200 text-indigo-600 rounded-lg hover:bg-indigo-50 flex items-center gap-1" title="Ver tour do Cube">
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z" />
            </svg>
            Tour
          </button>
          <button onClick={newReport} className="px-3 py-1.5 text-xs border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-1">
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
            </svg>
            Novo
          </button>
          <button onClick={saveReport} disabled={rowFields.length === 0 && colFields.length === 0} className="px-3 py-1.5 text-xs bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1">
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M17.593 3.322c1.1.128 1.907 1.077 1.907 2.185V21L12 17.25 4.5 21V5.507c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0111.186 0z" />
            </svg>
            Salvar
          </button>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden relative">
        {/* LEFT PANEL */}
        <div className={`${sidebarCollapsed ? 'w-10' : 'w-72'} border-r bg-gray-50 flex flex-col overflow-hidden transition-all duration-200`}>
          {sidebarCollapsed ? (
            <div className="flex-1 flex flex-col items-center pt-3 gap-3 cursor-pointer" onClick={() => setSidebarCollapsed(false)}>
              <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
              </svg>
            </div>
          ) : (
          <>
          {/* Report Name */}
          <div className="p-3 border-b">
            <input type="text" value={reportName} onChange={(e) => setReportName(e.target.value)} className="w-full px-2 py-1.5 text-sm font-medium border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" placeholder="Nome do relatorio" />
          </div>

          {/* Saved Reports */}
          {savedReports.length > 0 && (
            <div className="p-3 border-b">
              <p className="text-[0.65rem] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Relatorios Salvos</p>
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {savedReports.map(r => (
                  <div key={r.id} className={`flex items-center justify-between p-1.5 rounded text-xs cursor-pointer hover:bg-white ${selectedReportId === r.id ? 'bg-indigo-50 text-indigo-700 font-medium' : 'text-gray-600'}`}>
                    <span onClick={() => loadReport(r)} className="truncate flex-1">{r.name}</span>
                    <button onClick={(e) => { e.stopPropagation(); deleteReport(r.id); }} className="text-red-400 hover:text-red-600 ml-1">
                      <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Flow Filter */}
          <div className="p-3 border-b">
            <p className="text-[0.65rem] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Fluxo</p>
            <select value={selectedFlowId} onChange={(e) => setSelectedFlowId(e.target.value)} className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg bg-white">
              <option value="">Todos os fluxos</option>
              {flows.map(f => (
                <option key={f.id} value={f.id}>{f.nome || f.name}</option>
              ))}
            </select>
          </div>

          {/* Search + Role Filter */}
          <div className="p-3 border-b space-y-2">
            <div className="relative">
              <svg className="h-3.5 w-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
              </svg>
              <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Buscar campo..." className="w-full pl-7 pr-2 py-1.5 text-xs border border-gray-300 rounded-lg" />
            </div>
            <div className="flex gap-1">
              <button onClick={() => setRoleFilter('all')} className={`flex-1 text-[0.6rem] py-1 px-1.5 rounded font-medium transition-colors ${roleFilter === 'all' ? 'bg-gray-700 text-white' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'}`}>
                Todos ({filteredFields.length})
              </button>
              <button onClick={() => setRoleFilter('dimension')} className={`flex-1 text-[0.6rem] py-1 px-1.5 rounded font-medium transition-colors ${roleFilter === 'dimension' ? 'bg-blue-600 text-white' : 'bg-blue-100 text-blue-700 hover:bg-blue-200'}`}>
                Dimensoes ({dimensionCount})
              </button>
              <button onClick={() => setRoleFilter('metric')} className={`flex-1 text-[0.6rem] py-1 px-1.5 rounded font-medium transition-colors ${roleFilter === 'metric' ? 'bg-amber-600 text-white' : 'bg-amber-100 text-amber-700 hover:bg-amber-200'}`}>
                Metricas ({metricCount})
              </button>
            </div>
          </div>

          {/* Available Fields List */}
          <div className="flex-1 overflow-y-auto p-3">
            <p className="text-[0.65rem] font-semibold text-gray-500 uppercase tracking-wider mb-2">
              Campos Disponiveis ({filteredFields.length})
            </p>

            <div className="space-y-3">
              {Object.entries(groupedFields).map(([groupName, fields]) => (
                <div key={groupName}>
                  <p className="text-[0.6rem] font-semibold text-gray-500 uppercase mb-1 flex items-center gap-1">
                    {groupName}
                    <span className="text-[0.5rem] font-normal text-gray-400">({fields.length})</span>
                  </p>
                  <div className="space-y-0.5">
                    {fields.map(field => (
                      <SourceFieldItem key={field.id} field={field} addFieldToZone={addFieldToZone} />
                    ))}
                  </div>
                </div>
              ))}
              {filteredFields.length === 0 && (
                <p className="text-xs text-gray-400 text-center py-4">Nenhum campo encontrado</p>
              )}
            </div>
          </div>
          {/* Collapse button */}
          <div className="p-2 border-t bg-gray-50">
            <button
              onClick={() => setSidebarCollapsed(true)}
              className="w-full flex items-center justify-center gap-1.5 py-1.5 text-[0.65rem] text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded transition-colors"
              title="Recolher painel"
            >
              <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
              </svg>
              Recolher
            </button>
          </div>
          </>
          )}
        </div>

        {/* CENTER - Builder + Results */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Builder Area */}
          <div className={`${showResults ? 'h-auto' : 'flex-1'} p-5 overflow-y-auto border-b`}>
            {/* Toggle buttons */}
            <div className="flex items-center gap-2 mb-3">
              <button
                onClick={() => setShowZones(!showZones)}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
                  showZones ? 'bg-indigo-50 border-indigo-200 text-indigo-700' : 'bg-gray-50 border-gray-200 text-gray-500 hover:bg-gray-100'
                }`}
              >
                <svg className={`h-3.5 w-3.5 transition-transform ${showZones ? '' : '-rotate-90'}`} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                </svg>
                Linhas / Colunas / Valores
                <span className="text-[0.6rem] opacity-60">({rowFields.length + colFields.length + valueFields.length})</span>
              </button>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
                  showFilters ? 'bg-indigo-50 border-indigo-200 text-indigo-700' : 'bg-gray-50 border-gray-200 text-gray-500 hover:bg-gray-100'
                }`}
              >
                <svg className={`h-3.5 w-3.5 transition-transform ${showFilters ? '' : '-rotate-90'}`} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 3c2.755 0 5.455.232 8.083.678.533.09.917.556.917 1.096v1.044a2.25 2.25 0 01-.659 1.591l-5.432 5.432a2.25 2.25 0 00-.659 1.591v2.927a2.25 2.25 0 01-1.244 2.013L9.75 21v-6.568a2.25 2.25 0 00-.659-1.591L3.659 7.409A2.25 2.25 0 013 5.818V4.774c0-.54.384-1.006.917-1.096A48.32 48.32 0 0112 3z" />
                </svg>
                Filtros
                {filters.filter(f => f.fieldId).length > 0 && (
                  <span className="text-[0.6rem] bg-indigo-100 text-indigo-700 px-1.5 rounded-full">{filters.filter(f => f.fieldId).length}</span>
                )}
              </button>
            </div>

            {/* Drop Zones Grid */}
            {showZones && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
              <DropZone
                id="rows"
                title="Linhas"
                subtitle="Agrupar por"
                helpText="Campos de texto/categoria. Cada valor diferente vira uma linha."
                color="blue"
                icon={<svg className="h-4 w-4 text-blue-600" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M3.75 12h16.5m-16.5 3.75h16.5M3.75 19.5h16.5M5.625 4.5h12.75a1.875 1.875 0 010 3.75H5.625a1.875 1.875 0 010-3.75z" /></svg>}
                fields={rowFields}
                onRemoveField={(id) => removeFieldFromZone(id, 'rows')}
                activeField={activeField}
              />
              <DropZone
                id="columns"
                title="Colunas"
                subtitle="Dividir por"
                helpText="Campos de texto/categoria. Cada valor vira uma coluna no relatorio."
                color="green"
                icon={<svg className="h-4 w-4 text-green-600" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M9 4.5v15m6-15v15m-10.875 0h15.75c.621 0 1.125-.504 1.125-1.125V5.625c0-.621-.504-1.125-1.125-1.125H4.125C3.504 4.5 3 5.004 3 5.625v12.75c0 .621.504 1.125 1.125 1.125z" /></svg>}
                fields={colFields}
                onRemoveField={(id) => removeFieldFromZone(id, 'columns')}
                activeField={activeField}
              />
              <DropZone
                id="values"
                title="Valores"
                subtitle="Medir / Exibir"
                helpText="Campos numericos (horas, %, qtd). Dados que serao exibidos nas celulas."
                color="amber"
                icon={<svg className="h-4 w-4 text-amber-600" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 15.75V18m-7.5-6.75h.008v.008H8.25v-.008zm0 2.25h.008v.008H8.25V13.5zm0 2.25h.008v.008H8.25v-.008zm0 2.25h.008v.008H8.25V18zm2.498-6.75h.007v.008h-.007v-.008zm0 2.25h.007v.008h-.007V13.5zm0 2.25h.007v.008h-.007v-.008zm0 2.25h.007v.008h-.007V18zm2.504-6.75h.008v.008h-.008v-.008zm0 2.25h.008v.008h-.008V13.5zm0 2.25h.008v.008h-.008v-.008zm0 2.25h.008v.008h-.008V18zm2.498-6.75h.008v.008h-.008v-.008zm0 2.25h.008v.008h-.008V13.5zM8.25 6h7.5v2.25h-7.5V6zM12 2.25c-1.892 0-3.758.11-5.593.322C5.307 2.7 4.5 3.65 4.5 4.757V19.5a2.25 2.25 0 002.25 2.25h10.5a2.25 2.25 0 002.25-2.25V4.757c0-1.108-.806-2.057-1.907-2.185A48.507 48.507 0 0012 2.25z" /></svg>}
                fields={valueFields}
                onRemoveField={(id) => removeFieldFromZone(id, 'values')}
                activeField={activeField}
              />
            </div>
            )}

            {/* Filters */}
            {showFilters && (
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xs font-semibold text-gray-600 uppercase tracking-wider flex items-center gap-1.5">
                  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 3c2.755 0 5.455.232 8.083.678.533.09.917.556.917 1.096v1.044a2.25 2.25 0 01-.659 1.591l-5.432 5.432a2.25 2.25 0 00-.659 1.591v2.927a2.25 2.25 0 01-1.244 2.013L9.75 21v-6.568a2.25 2.25 0 00-.659-1.591L3.659 7.409A2.25 2.25 0 013 5.818V4.774c0-.54.384-1.006.917-1.096A48.32 48.32 0 0112 3z" />
                  </svg>
                  Filtros
                </h3>
                <button onClick={() => setFilters(prev => [...prev, { fieldId: '', operator: 'igual', value: '' }])} className="text-xs text-indigo-600 hover:text-indigo-800 flex items-center gap-1">
                  <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                  </svg>
                  Adicionar filtro
                </button>
              </div>
              {filters.length > 0 && (
                <div className="space-y-1.5">
                  {filters.map((filter, i) => (
                    <FilterRow
                      key={i}
                      filter={filter}
                      availableFields={availableFields}
                      onChange={(f) => { const nf = [...filters]; nf[i] = f; setFilters(nf); }}
                      onRemove={() => setFilters(prev => prev.filter((_, idx) => idx !== i))}
                    />
                  ))}
                </div>
              )}
            </div>
            )}

            {/* Generate Button */}
            <div className="flex items-center gap-3">
              <button onClick={generateReport} disabled={isLoading || (rowFields.length === 0 && colFields.length === 0)} className="px-5 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2 shadow-sm">
                {isLoading ? (
                  <>
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                    </svg>
                    Gerando...
                  </>
                ) : (
                  <>
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.348a1.125 1.125 0 010 1.971l-11.54 6.347a1.125 1.125 0 01-1.667-.985V5.653z" />
                    </svg>
                    Gerar Relatorio
                  </>
                )}
              </button>
              {hasGenerated && tableData.length > 0 && (
                <button onClick={exportCSV} className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2">
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                  </svg>
                  Exportar CSV
                </button>
              )}
              {hasGenerated && (
                <span className="text-xs text-gray-500">
                  {tableData.length} resultado{tableData.length !== 1 ? 's' : ''}
                </span>
              )}
            </div>
          </div>

          {/* Results Table */}
          {showResults && (
            <div className="flex-1 overflow-auto">
              {isLoading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <svg className="animate-spin h-8 w-8 text-indigo-600 mx-auto mb-3" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                    </svg>
                    <p className="text-sm text-gray-500">Processando dados...</p>
                  </div>
                </div>
              ) : tableData.length === 0 ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <svg className="h-12 w-12 text-gray-300 mx-auto mb-3" fill="none" viewBox="0 0 24 24" strokeWidth={1} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3.375 19.5h17.25m-17.25 0a1.125 1.125 0 01-1.125-1.125M3.375 19.5h7.5c.621 0 1.125-.504 1.125-1.125m-9.75 0V5.625m0 12.75v-1.5c0-.621.504-1.125 1.125-1.125m18.375 2.625V5.625m0 12.75c0 .621-.504 1.125-1.125 1.125m1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125m0 3.75h-7.5A1.125 1.125 0 0112 18.375m9.75-12.75c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125m19.5 0v1.5c0 .621-.504 1.125-1.125 1.125M2.25 5.625v1.5c0 .621.504 1.125 1.125 1.125m0 0h17.25m-17.25 0h7.5c.621 0 1.125.504 1.125 1.125M3.375 8.25c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125m17.25-3.75h-7.5c-.621 0-1.125.504-1.125 1.125m8.625-1.125c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125m-17.25 0h7.5m-7.5 0c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125M12 10.875v-1.5m0 1.5c0 .621-.504 1.125-1.125 1.125M12 10.875c0 .621.504 1.125 1.125 1.125m-2.25 0c.621 0 1.125.504 1.125 1.125M13.125 12h7.5m-7.5 0c-.621 0-1.125.504-1.125 1.125M20.625 12c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125m-17.25 0h7.5M12 14.625v-1.5m0 1.5c0 .621-.504 1.125-1.125 1.125M12 14.625c0 .621.504 1.125 1.125 1.125m-2.25 0c.621 0 1.125.504 1.125 1.125m0 0v.375" />
                    </svg>
                    <p className="text-sm text-gray-500">Nenhum resultado encontrado</p>
                    <p className="text-xs text-gray-400 mt-1">Ajuste os campos ou filtros e tente novamente</p>
                  </div>
                </div>
              ) : (
                <div className="p-4">
                  <p className="text-[0.6rem] text-gray-400 mb-2">Arraste os cabecalhos para reordenar as colunas</p>
                  <div className="overflow-x-auto rounded-lg border border-gray-200">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <SortableContext items={columnOrder.map(h => `col-header-${h}`)} strategy={horizontalListSortingStrategy}>
                          <tr>
                            {columnOrder.map((header) => (
                              <SortableTableHeader key={header} id={`col-header-${header}`} header={header} />
                            ))}
                          </tr>
                        </SortableContext>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-100">
                        {tableData.map((row, rowIdx) => (
                          <tr key={rowIdx} className="hover:bg-gray-50">
                            {columnOrder.map((col) => {
                              const val = row[col];
                              return (
                                <td key={col} className="px-4 py-2 text-sm text-gray-700 whitespace-nowrap">
                                  {val !== null && val !== undefined ? String(val) : '-'}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Empty state */}
          {!showResults && rowFields.length === 0 && colFields.length === 0 && valueFields.length === 0 && (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center max-w-lg">
                <div className="mx-auto mb-4 p-4 bg-indigo-50 rounded-2xl w-fit">
                  <svg className="h-12 w-12 text-indigo-400" fill="none" viewBox="0 0 24 24" strokeWidth={1} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 7.5l-9-5.25L3 7.5m18 0l-9 5.25m9-5.25v9l-9 5.25M3 7.5l9 5.25M3 7.5v9l9 5.25m0-9v9" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-700 mb-2">Monte seu relatorio</h3>
                <p className="text-sm text-gray-500 mb-4">
                  Arraste ou clique nos campos do painel esquerdo. Cada campo mostra apenas
                  os botoes das zonas compativeis com seu tipo.
                </p>
                <div className="bg-gray-50 rounded-lg p-4 text-left text-xs text-gray-600 space-y-2.5">
                  <div className="flex items-start gap-2">
                    <span className="inline-block mt-0.5 w-4 h-4 rounded bg-blue-200 flex-shrink-0 text-center text-[0.6rem] leading-4 font-bold text-blue-700">L</span>
                    <div><b>Linhas</b> — Campos de texto/categoria (Responsavel, Etapa, Status...). Cada valor diferente vira uma <b>linha</b> na tabela.</div>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="inline-block mt-0.5 w-4 h-4 rounded bg-green-200 flex-shrink-0 text-center text-[0.6rem] leading-4 font-bold text-green-700">C</span>
                    <div><b>Colunas</b> — Mesmos tipos de Linhas, mas cada valor diferente vira uma <b>coluna</b>. Bom para cruzar dados (ex: Status x Responsavel).</div>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="inline-block mt-0.5 w-4 h-4 rounded bg-amber-200 flex-shrink-0 text-center text-[0.6rem] leading-4 font-bold text-amber-700">V</span>
                    <div><b>Valores</b> — Campos numericos (Tempo, Horas, %, Qtd...). Sao os <b>dados medidos</b> que aparecem nas celulas da tabela.</div>
                  </div>
                </div>
                <p className="text-[0.65rem] text-gray-400 mt-3">
                  Exemplo: Linhas = Responsavel, Colunas = Status SLA, Valores = Horas em Atraso
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>

    {/* Drag Overlay */}
    <DragOverlay>
      {activeField ? <FieldChip field={activeField} isDragging /> : null}
    </DragOverlay>

    {/* ═══ CUBE TOUR MODAL ═══ */}
    {showTour && (() => {
      const step = tourSteps[tourStep];
      const progress = ((tourStep + 1) / tourSteps.length) * 100;

      const visualColors: Record<string, { bg: string; text: string; gradient: string }> = {
        intro:    { bg: 'from-indigo-500 to-purple-600', text: 'text-indigo-100', gradient: 'from-indigo-50 to-purple-50' },
        fields:   { bg: 'from-blue-500 to-cyan-500', text: 'text-blue-100', gradient: 'from-blue-50 to-cyan-50' },
        zones:    { bg: 'from-emerald-500 to-teal-500', text: 'text-emerald-100', gradient: 'from-emerald-50 to-teal-50' },
        filters:  { bg: 'from-violet-500 to-fuchsia-500', text: 'text-violet-100', gradient: 'from-violet-50 to-fuchsia-50' },
        generate: { bg: 'from-orange-500 to-red-500', text: 'text-orange-100', gradient: 'from-orange-50 to-red-50' },
        save:     { bg: 'from-sky-500 to-blue-600', text: 'text-sky-100', gradient: 'from-sky-50 to-blue-50' },
        tip:      { bg: 'from-amber-400 to-orange-500', text: 'text-amber-100', gradient: 'from-amber-50 to-orange-50' },
      };
      const colors = visualColors[step.visual] || visualColors.intro;

      return (
        <div className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-md flex items-center justify-center" onClick={(e) => { if (e.target === e.currentTarget) setShowTour(false); }}>
          <div className="relative w-full max-w-2xl max-h-[92vh] mx-4 flex flex-col" style={{ animation: 'fadeInScale 0.3s ease-out' }}>

            {/* Close button */}
            <button onClick={() => setShowTour(false)} className="absolute -top-2 -right-2 z-10 w-10 h-10 bg-gray-800 hover:bg-gray-700 text-white rounded-full flex items-center justify-center transition-all shadow-lg border border-gray-600">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
            </button>

            {/* Main card */}
            <div className="bg-white rounded-2xl overflow-hidden shadow-2xl flex flex-col max-h-[92vh]">

              {/* Progress bar */}
              <div className="h-1 bg-gray-100 flex-shrink-0">
                <div className={`h-full bg-gradient-to-r ${colors.bg} transition-all duration-700 ease-out`} style={{ width: `${progress}%` }} />
              </div>

              {/* Auto-advance bar */}
              {!tourPaused && tourStep < tourSteps.length - 1 && (
                <div className="h-0.5 bg-transparent flex-shrink-0 overflow-hidden">
                  <div className="h-full bg-indigo-400" style={{ animation: 'tourAutoProgress 14s linear', width: '0%' }} key={`progress-${tourStep}`} />
                </div>
              )}

              {/* Content */}
              <div className="flex-1 overflow-y-auto" key={`slide-${tourStep}`} style={{ animation: 'slideIn 0.4s ease-out' }}>
                <div className="px-8 py-10 text-center">
                  {/* Icon */}
                  <div className={`inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br ${colors.bg} shadow-xl mb-6`} style={{ animation: 'fadeInScale 0.5s ease-out 0.1s both' }}>
                    <span className="text-4xl">{step.icon}</span>
                  </div>

                  {/* Title */}
                  <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1" style={{ animation: 'fadeInUp 0.5s ease-out 0.2s both' }}>{step.title}</h2>
                  <p className="text-sm text-gray-400 mb-5" style={{ animation: 'fadeInUp 0.5s ease-out 0.25s both' }}>{step.subtitle}</p>

                  {/* Content text */}
                  <p className="text-gray-600 text-base max-w-xl mx-auto leading-relaxed mb-6" style={{ animation: 'fadeInUp 0.5s ease-out 0.3s both' }}>{step.content}</p>

                  {/* Visual hints per step */}
                  {step.visual === 'zones' && (
                    <div className="flex items-center justify-center gap-3 flex-wrap" style={{ animation: 'fadeInUp 0.5s ease-out 0.4s both' }}>
                      {[
                        { label: 'Linhas', color: 'bg-blue-100 text-blue-700 border-blue-300', icon: '📊' },
                        { label: 'Colunas', color: 'bg-green-100 text-green-700 border-green-300', icon: '📐' },
                        { label: 'Valores', color: 'bg-amber-100 text-amber-700 border-amber-300', icon: '🔢' },
                      ].map((z, i) => (
                        <React.Fragment key={z.label}>
                          {i > 0 && <span className="text-gray-300">→</span>}
                          <div className={`px-4 py-2 rounded-xl text-sm font-medium border ${z.color}`}>
                            {z.icon} {z.label}
                          </div>
                        </React.Fragment>
                      ))}
                    </div>
                  )}

                  {step.visual === 'tip' && (
                    <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-5 max-w-md mx-auto text-left" style={{ animation: 'fadeInUp 0.5s ease-out 0.4s both' }}>
                      <div className="space-y-2 text-sm text-amber-900">
                        <div className="flex items-center gap-2"><span className="w-5 h-5 rounded bg-blue-200 text-center text-[0.6rem] leading-5 font-bold text-blue-700">L</span> Responsável</div>
                        <div className="flex items-center gap-2"><span className="w-5 h-5 rounded bg-green-200 text-center text-[0.6rem] leading-5 font-bold text-green-700">C</span> Status SLA</div>
                        <div className="flex items-center gap-2"><span className="w-5 h-5 rounded bg-amber-200 text-center text-[0.6rem] leading-5 font-bold text-amber-700">V</span> Horas em Atraso</div>
                      </div>
                      <p className="text-xs text-amber-600 mt-3 pt-3 border-t border-amber-200">= Tabela mostrando atraso por pessoa e status</p>
                    </div>
                  )}

                  {step.visual === 'intro' && (
                    <div className="flex items-center justify-center gap-2 mt-2" style={{ animation: 'fadeInUp 0.5s ease-out 0.4s both' }}>
                      {['Responsável', 'Status', 'SLA', 'Tempo', 'Etapa'].map((tag, i) => (
                        <span key={tag} className="px-3 py-1.5 bg-indigo-50 text-indigo-600 text-xs font-medium rounded-lg border border-indigo-100" style={{ animation: `fadeInUp 0.4s ease-out ${0.5 + i * 0.08}s both` }}>{tag}</span>
                      ))}
                    </div>
                  )}

                  {step.visual === 'fields' && (
                    <div className="flex items-center justify-center gap-2 flex-wrap" style={{ animation: 'fadeInUp 0.5s ease-out 0.4s both' }}>
                      {[
                        { label: 'Dimensões', count: '296', color: 'bg-blue-50 text-blue-600 border-blue-200' },
                        { label: 'Métricas', count: '227', color: 'bg-amber-50 text-amber-600 border-amber-200' },
                      ].map(b => (
                        <span key={b.label} className={`px-4 py-2 text-sm font-medium rounded-xl border ${b.color}`}>{b.label} ({b.count})</span>
                      ))}
                    </div>
                  )}

                  {step.visual === 'generate' && (
                    <div className="flex items-center justify-center gap-3" style={{ animation: 'fadeInUp 0.5s ease-out 0.4s both' }}>
                      <span className="px-5 py-2.5 bg-indigo-600 text-white text-sm font-medium rounded-lg shadow-sm">▶ Gerar Relatório</span>
                      <span className="text-gray-300">→</span>
                      <span className="px-5 py-2.5 border border-gray-200 text-gray-600 text-sm rounded-lg">📥 Exportar CSV</span>
                    </div>
                  )}

                  {/* Final step CTA */}
                  {tourStep === tourSteps.length - 1 && (
                    <div className="mt-6" style={{ animation: 'fadeInUp 0.5s ease-out 0.5s both' }}>
                      <button onClick={() => setShowTour(false)} className="px-6 py-3 bg-gray-900 text-white rounded-xl font-semibold hover:bg-black transition-colors shadow-lg">
                        Começar a usar o Cube →
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Bottom navigation */}
              <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between flex-shrink-0 bg-white">
                {/* Left: play/pause + counter */}
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setTourPaused(!tourPaused)}
                    className="w-9 h-9 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
                    title={tourPaused ? 'Reproduzir' : 'Pausar'}
                  >
                    {tourPaused ? (
                      <svg className="w-4 h-4 text-gray-600" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
                    ) : (
                      <svg className="w-4 h-4 text-gray-600" fill="currentColor" viewBox="0 0 24 24"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/></svg>
                    )}
                  </button>
                  <span className="text-xs text-gray-400 font-medium">{tourStep + 1} / {tourSteps.length}</span>
                </div>

                {/* Center: dots */}
                <div className="flex gap-1.5">
                  {tourSteps.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => { setTourStep(i); setTourPaused(true); }}
                      className={`h-1.5 rounded-full transition-all ${
                        i === tourStep ? 'w-6 bg-gray-900' : i < tourStep ? 'w-1.5 bg-gray-400' : 'w-1.5 bg-gray-200 hover:bg-gray-300'
                      }`}
                    />
                  ))}
                </div>

                {/* Right: prev/next */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => { setTourStep(Math.max(0, tourStep - 1)); setTourPaused(true); }}
                    disabled={tourStep === 0}
                    className="w-9 h-9 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors disabled:opacity-30"
                  >
                    <svg className="w-4 h-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                  </button>
                  <button
                    onClick={() => {
                      if (tourStep < tourSteps.length - 1) {
                        setTourStep(tourStep + 1);
                        setTourPaused(true);
                      } else {
                        setShowTour(false);
                      }
                    }}
                    className={`px-4 py-2 rounded-full text-xs font-semibold transition-all ${
                      tourStep < tourSteps.length - 1
                        ? 'bg-gray-900 text-white hover:bg-black'
                        : 'bg-green-600 text-white hover:bg-green-700'
                    }`}
                  >
                    {tourStep < tourSteps.length - 1 ? 'Próximo →' : '✓ Concluir'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    })()}

    </DndContext>
  );
};

export default Cube;
